// ============================================================
// TANGOALPHA BT — surface explorer.
//
// Pick a date and tenor; see the smile at every expiry pillar, the ATM term
// structure, and what a seasoned position reads between pillars. Useful for
// sanity-checking the calibration that feeds every backtest — the engine can be
// perfectly self-consistent and still be reading a bad surface.
//
// The smile chart plots vol against strike OFFSET from the forward, not against
// absolute strike, so pillars with different forwards are comparable on one axis.
// ============================================================
import React, { useEffect, useMemo, useState } from 'react';
import { C, F, MONO } from './theme';
import { S, HEADER_H } from './ui';
import { BTChart, Legend, Panel, Field, fullInput, fullSelect, fmt } from './btShared';
import { dayNumFromISO } from '../backtest/core';
import { RoughSABREngine, SABREngine } from '../backtest/engines';
import { getSlice, valueOption } from '../backtest/surface';
import { loadSurface } from '../backtest/surfaceData';
import { surfaceDataUrl } from '../backtest/dataUrl';

const OFFSETS = Array.from({ length: 41 }, (_, i) => -200 + i * 10); // bp
const SLOTS = [C.gn, C.rd, C.cy, C.ac, C.pu, C.bl];

const panelPad = { padding: '12px 14px', borderBottom: `1px solid ${C.bd}` };
const rightScroll = {
  ...S.rightPanel,
  position: 'sticky',
  top: HEADER_H,
  alignSelf: 'start',
  maxHeight: `calc(100vh - ${HEADER_H}px)`,
};

export default function BTSurface() {
  const [surface, setSurface] = useState(null);
  const [error, setError] = useState(null);
  const [dateIx, setDateIx] = useState(0);
  const [tenor, setTenor] = useState('10Y');
  const [engineName, setEngineName] = useState('sabr');

  useEffect(() => {
    let cancelled = false;
    const url = surfaceDataUrl();
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`could not load ${url} (${r.status})`);
        return r.json();
      })
      .then((p) => {
        if (cancelled) return;
        const s = loadSurface(p);
        setSurface(s);
        setDateIx(s.snapshots.length - 1);
      })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, []);

  const engine = useMemo(
    () => (engineName === 'sabr' ? new SABREngine() : new RoughSABREngine()),
    [engineName],
  );

  const snap = surface ? surface.snapshots[dateIx] : null;

  // Smile per pillar, in strike offset from that pillar's own forward.
  const smile = useMemo(() => {
    if (!snap) return null;
    const slice = getSlice(snap, tenor);
    const rows = OFFSETS.map((bp) => {
      const row = { x: bp };
      slice.buckets.forEach((b) => {
        try {
          row[b.expiry] = engine.impliedVol(b, b.F + bp * 1e-4) * 1e4;
        } catch {
          row[b.expiry] = null; // outside the shifted-SABR domain; a real hole
        }
      });
      return row;
    });
    return { rows, buckets: slice.buckets };
  }, [snap, tenor, engine]);

  // Term structure of the ATM level and the two wings.
  //
  // Deliberately NOT "SABR ATM vs rough-SABR ATM": both models are anchored to
  // the same ATM quote, so those two lines coincide exactly and the chart would
  // show one series pretending to be two. The wings are where they differ.
  const term = useMemo(() => {
    if (!snap) return null;
    const slice = getSlice(snap, tenor);
    const at = (b, bp) => {
      try { return engine.impliedVol(b, b.F + bp * 1e-4) * 1e4; } catch { return null; }
    };
    return slice.buckets.map((b) => ({
      x: b.T, lo: at(b, -100), atm: at(b, 0), hi: at(b, 100),
    }));
  }, [snap, tenor, engine]);

  const atm = useMemo(() => {
    if (!smile) return [];
    return smile.buckets.map((b) => {
      let v = null;
      try { v = engine.impliedVol(b, b.F) * 1e4; } catch { v = null; }
      return { b, v };
    });
  }, [smile, engine]);

  if (error) {
    return (
      <div style={{ ...panelPad, color: C.rd, fontSize: F.md }}>
        {error}
        <div style={{ color: C.txM, fontSize: F.xs, marginTop: 6 }}>
          Regenerate the payload with <code>python -m utils.bake_web_data</code> and copy it to{' '}
          <code>public/data/surface.json</code>.
        </div>
      </div>
    );
  }
  if (!surface || !snap || !smile || !term) {
    return <div style={{ ...panelPad, color: C.txD, fontSize: F.md }}>Loading the vol cube…</div>;
  }

  const asOf = surface.dates[dateIx];

  return (
    <div style={S.pageContent}>
      <div style={S.leftCol}>
        <Panel
          title={`Smile — ${tenor}, ${asOf}`}
          subtitle="Normal implied vol in bp against strike offset from each pillar's own forward, so the pillars are comparable on one axis."
          note="A gap in a line is a strike outside the shifted-SABR domain (K + λ ≤ 0) — shown as a hole rather than silently interpolated over, which is what a NaN used to do here."
        >
          <BTChart
            rows={smile.rows}
            xKey="x"
            xNumeric
            xFormat={(v) => `${v > 0 ? '+' : ''}${v.toFixed(0)}`}
            zeroLine={false}
            height={320}
            yWidth={52}
            yFormat={(v) => `${v.toFixed(0)}bp`}
            series={smile.buckets.map((b, i) => ({
              key: b.expiry, label: b.expiry, color: SLOTS[i % SLOTS.length], width: 1.6,
            }))}
          />
          <Legend items={atm.map(({ b, v }, i) => ({
            label: b.expiry,
            color: SLOTS[i % SLOTS.length],
            value: v == null ? '--' : `${v.toFixed(1)}bp`,
          }))} />
        </Panel>

        <Panel
          title={`Vol term structure — ${tenor}`}
          note="Plotted against the actual ACT/365 time to each pillar's expiry, not the nominal label. Switching the smile model changes the wings but not the ATM line: both models are anchored to the same ATM quote, so they agree there by construction. The wings are where they disagree, and what the Compare tab prices."
        >
          <BTChart
            rows={term}
            xKey="x"
            xNumeric
            xFormat={(v) => `${v.toFixed(2)}y`}
            zeroLine={false}
            height={230}
            yWidth={52}
            yFormat={(v) => `${v.toFixed(0)}bp`}
            series={[
              { key: 'lo',  label: 'F −100bp', color: C.pu },
              { key: 'atm', label: 'ATM',      color: C.gn },
              { key: 'hi',  label: 'F +100bp', color: C.rd },
            ]}
          />
          <Legend items={[
            { label: 'F −100bp', color: C.pu },
            { label: 'ATM',      color: C.gn },
            { label: 'F +100bp', color: C.rd },
          ]} />
        </Panel>

        <Panel
          title="Interpolated term slice"
          subtitle="What a seasoned position actually reads: the forward, PV01 and ATM vol at arbitrary remaining life, from the same interpolator the backtester uses."
          note="At T = 0 the forward is linearly extrapolated to spot — that is the rate an option actually fixes against. The previous engines settled at the original pillar's forward-starting rate instead, which on a sloping curve is a different rate entirely."
        >
          <InterpTable snap={snap} tenor={tenor} />
        </Panel>
      </div>

      <div style={rightScroll}>
        <div style={panelPad}>
          <div style={{ ...S.label, marginBottom: 8 }}>View</div>

          <Field label={`Date — ${asOf} (${dateIx + 1} of ${surface.dates.length})`}>
            <input
              type="range" min={0} max={surface.dates.length - 1} value={dateIx}
              onChange={(e) => setDateIx(Number(e.target.value))}
              style={{ width: '100%', accentColor: C.gn }}
            />
          </Field>

          <Field label="Jump to a date">
            <input
              type="date" value={asOf}
              min={surface.dates[0]} max={surface.dates[surface.dates.length - 1]}
              onChange={(e) => {
                const want = dayNumFromISO(e.target.value);
                let best = 0, bestD = Infinity;
                surface.days.forEach((d, i) => {
                  const dd = Math.abs(d - want);
                  if (dd < bestD) { bestD = dd; best = i; }
                });
                setDateIx(best);
              }}
              style={fullInput}
            />
          </Field>

          <Field label="Underlying tenor">
            <select value={tenor} onChange={(e) => setTenor(e.target.value)} style={fullSelect}>
              {surface.tenors.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>

          <Field label="Smile model">
            <select value={engineName} onChange={(e) => setEngineName(e.target.value)} style={fullSelect}>
              <option value="sabr">SABR (Hagan-West)</option>
              <option value="rough">Rough SABR</option>
            </select>
          </Field>
        </div>

        <div style={panelPad}>
          <div style={{ ...S.label, marginBottom: 8 }}>Pillars</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.bd}` }}>
                <th style={{ ...S.th, textAlign: 'left', padding: '4px 4px' }}>Exp</th>
                <th style={{ ...S.th, textAlign: 'right', padding: '4px 4px' }}>T</th>
                <th style={{ ...S.th, textAlign: 'right', padding: '4px 4px' }}>F %</th>
                <th style={{ ...S.th, textAlign: 'right', padding: '4px 4px' }}>PV01</th>
                <th style={{ ...S.th, textAlign: 'right', padding: '4px 4px' }}>ATM bp</th>
              </tr>
            </thead>
            <tbody>
              {atm.map(({ b, v }, i) => (
                <tr key={b.expiry}>
                  <td style={{ ...S.td, padding: '4px', color: SLOTS[i % SLOTS.length] }}>{b.expiry}</td>
                  <td style={{ ...S.tdNum, padding: '4px' }}>{b.T.toFixed(4)}</td>
                  <td style={{ ...S.tdNum, padding: '4px' }}>{(b.F * 100).toFixed(3)}</td>
                  <td style={{ ...S.tdNum, padding: '4px' }}>{b.annuity.toFixed(3)}</td>
                  <td style={{ ...S.tdNum, padding: '4px', color: C.tx }}>{v == null ? '--' : v.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: F.xxs, color: C.txM, marginTop: 8, lineHeight: 1.5 }}>
            <span style={{ fontFamily: MONO }}>T</span> is the actual ACT/365 time to the pillar's
            adjusted expiry, not the nominal tenor. <span style={{ fontFamily: MONO }}>PV01</span> is
            the market annuity — note it sits well below the swap tenor in years (24.7 against 30.0
            for 30Y).
          </div>
        </div>
      </div>
    </div>
  );
}

function InterpTable({ snap, tenor }) {
  const rows = useMemo(() => {
    const slice = getSlice(snap, tenor);
    const eng = new SABREngine();
    const Ts = [0, 0.02, 0.05, 0.0833, 0.125, 0.25, 0.375, 0.5, 0.75, 1.0];
    return Ts.map((T) => {
      const F_ = slice.forward(T);
      const rep = slice.bracketReport(T);
      let v = null, straddle = null;
      try {
        v = T > 0 ? slice.vol(eng, T, F_) * 1e4 : 0;
        straddle =
          valueOption(snap, eng, tenor, F_, 1, T, { greeks: false }).price +
          valueOption(snap, eng, tenor, F_, -1, T, { greeks: false }).price;
      } catch { /* leave null */ }
      return {
        T, F: F_, annuity: slice.annuity(T), v, straddle,
        pillars: rep.lowerPillar === rep.upperPillar
          ? rep.lowerPillar : `${rep.lowerPillar}–${rep.upperPillar}`,
        method: rep.method,
      };
    });
  }, [snap, tenor]);

  return (
    <div style={{ maxHeight: 340, overflow: 'auto', border: `1px solid ${C.bd}`, borderRadius: 4 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ position: 'sticky', top: 0, background: C.bgC, zIndex: 1 }}>
          <tr style={{ borderBottom: `1px solid ${C.bd}` }}>
            {['Remaining T', 'Forward (%)', 'PV01', 'ATM vol (bp)', 'Bracketing pillars', 'ATM straddle']
              .map((h, i) => (
                <th key={h} style={{ ...S.th, textAlign: i === 4 ? 'left' : 'right' }}>{h}</th>
              ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.T} style={{ borderBottom: `1px solid ${C.bd}44` }}>
              <td style={S.tdNum}>{r.T.toFixed(4)}</td>
              <td style={S.tdNum}>{(r.F * 100).toFixed(4)}</td>
              <td style={S.tdNum}>{r.annuity.toFixed(4)}</td>
              <td style={{ ...S.tdNum, color: C.tx }}>{r.v == null ? '--' : r.v.toFixed(2)}</td>
              <td style={S.td}>
                {r.pillars} <span style={{ color: C.txM }}>({r.method})</span>
              </td>
              <td style={S.tdNum}>{fmt(r.straddle, 6)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
